import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Twitter } from "lucide-react";
import { LOGOS } from "@/data/assets";

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Combat 100% Whey", to: "/products/combat-whey" },
  { label: "Combat Preworkout", to: "/products/combat-preworkout" },
  { label: "About MusclePharm", to: "/about" },
  { label: "Authenticity", to: "/authenticity" },
  { label: "Contact", to: "/contact" },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[color:var(--mp-dark)] text-white"
    >
      {/* Newsletter-style banner strip (replaces subscribe per brief) */}
      <div className="border-b border-white/10">
        <div className="mp-container py-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <div className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">
              We Live This.<br />
              <span style={{ color: "var(--mp-green)" }}>Combat In India.</span>
            </div>
            <p className="mt-4 max-w-2xl text-white/60 font-body text-base leading-relaxed">
              MusclePharm India delivers premium sports nutrition to serious athletes. Explore the Combat range and verify authenticity every time you buy.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end flex flex-wrap gap-3">
            <Link to="/products" data-testid="footer-cta-products" className="mp-btn mp-btn-green">
              Explore Products
            </Link>
            <Link to="/authenticity" data-testid="footer-cta-authenticity" className="mp-btn mp-btn-outline-light">
              Verify Authenticity
            </Link>
          </div>
        </div>
      </div>

      <div className="mp-container pt-16 pb-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand block */}
        <div className="md:col-span-5">
          {/* Footer logo */}
          <div className="flex items-center gap-3">
            <img
              src={LOGOS.footer}
              alt="MusclePharm"
              data-placeholder-label="Footer Logo"
              className="h-10 w-auto object-contain"
            />
            <span className="font-heading uppercase tracking-[0.3em] text-[10px] text-white/60 pl-3 border-l border-white/20 self-center h-6 inline-flex items-center">
              India · Official
            </span>
          </div>

          <p className="mt-6 max-w-md text-white/60 font-body leading-relaxed">
            Exclusively brought to India by MusclePro Nutrition Private Limited. Distributed across India by Nutriride and ADS Gallery.
          </p>

          <div className="mt-8 flex items-center gap-3" data-testid="footer-socials">
            {[
              { Icon: Instagram, label: "Instagram" },
              { Icon: Facebook, label: "Facebook" },
              { Icon: Youtube, label: "YouTube" },
              { Icon: Twitter, label: "Twitter" },
            ].map(({ Icon, label }) => (
              <a
                key={label}
                href="#"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="w-10 h-10 flex items-center justify-center border border-white/15 text-white/70 hover:border-[color:var(--mp-green)] hover:text-[color:var(--mp-green)] transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        {/* Quick links */}
        <div className="md:col-span-3">
          <h4 className="font-heading uppercase tracking-[0.28em] text-[11px] text-white/45 mb-5">
            Quick Links
          </h4>
          <ul className="space-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                  className="font-heading uppercase tracking-[0.12em] text-[13px] font-semibold text-white/80 hover:text-[color:var(--mp-green)] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-4">
          <h4 className="font-heading uppercase tracking-[0.28em] text-[11px] text-white/45 mb-5">
            Contact
          </h4>
          <ul className="space-y-3 text-white/80 font-body">
            <li>
              <span className="block font-heading uppercase tracking-[0.14em] text-[11px] text-white/45">
                Seller
              </span>
              <a
                href="mailto:info@adsgallery.com"
                data-testid="footer-email-seller"
                className="hover:text-[color:var(--mp-green)] transition-colors"
              >
                info@adsgallery.com
              </a>
            </li>
            <li>
              <span className="block font-heading uppercase tracking-[0.14em] text-[11px] text-white/45">
                Distributor
              </span>
              <a
                href="mailto:info@musclepronutrition.com"
                data-testid="footer-email-distributor"
                className="hover:text-[color:var(--mp-green)] transition-colors"
              >
                info@musclepronutrition.com
              </a>
            </li>
            <li>
              <span className="block font-heading uppercase tracking-[0.14em] text-[11px] text-white/45">
                Phone
              </span>
              <a
                href="tel:+919136048500"
                data-testid="footer-phone"
                className="hover:text-[color:var(--mp-green)] transition-colors"
              >
                +91 9136048500
              </a>
            </li>
            <li>
              <span className="block font-heading uppercase tracking-[0.14em] text-[11px] text-white/45">
                Address
              </span>
              <span className="block leading-relaxed">
                Unit No. 339, Building F, Kanakia Zillion, near CST Junction on LBS Marg.
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Disclaimer band */}
      <div className="border-t border-white/10">
        <div className="mp-container py-8 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8">
            <p className="text-white/50 text-sm leading-relaxed font-body">
              <span className="font-heading uppercase tracking-[0.2em] text-white/70 text-xs mr-2">
                Supplement Disclaimer:
              </span>
              These statements have not been evaluated by any regulatory authority. This product is not intended to diagnose, treat, cure, or prevent any disease. Not for use by persons under the age of 18 or those with pre-existing medical conditions. Consume as part of a balanced diet and healthy lifestyle.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end items-start gap-6">
            <Link
              to="/privacy"
              data-testid="footer-privacy"
              className="font-heading uppercase tracking-[0.14em] text-[11px] font-semibold text-white/70 hover:text-[color:var(--mp-green)]"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              data-testid="footer-terms"
              className="font-heading uppercase tracking-[0.14em] text-[11px] font-semibold text-white/70 hover:text-[color:var(--mp-green)]"
            >
              Terms & Conditions
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mp-container py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-white/40 text-xs font-heading uppercase tracking-[0.2em]">
            © {new Date().getFullYear()} MusclePharm India · All Rights Reserved
          </p>
          <p className="text-white/40 text-xs font-heading uppercase tracking-[0.2em]">
            Brought to India by MusclePro Nutrition Pvt Ltd
          </p>
        </div>
      </div>
    </footer>
  );
}
